import { useEffect, useRef, useState } from 'react'
import { DayPicker } from 'react-day-picker'
import { enAU } from 'date-fns/locale'
import { format } from 'date-fns'
import { CalendarDays, X } from 'lucide-react'
import { parseDueDate, toIsoDate } from '../lib/dates'

type Props = {
  value: string | null
  onChange: (value: string | null) => void
}

export function DueDateField({ value, onChange }: Props) {
  const [open, setOpen] = useState(false)
  const wrapper = useRef<HTMLDivElement>(null)
  const selected = parseDueDate(value)

  useEffect(() => {
    if (!open) return
    const close = (e: MouseEvent) => {
      if (!wrapper.current?.contains(e.target as Node)) setOpen(false)
    }
    const escape = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false)
    document.addEventListener('mousedown', close)
    document.addEventListener('keydown', escape)
    return () => {
      document.removeEventListener('mousedown', close)
      document.removeEventListener('keydown', escape)
    }
  }, [open])

  return (
    <div ref={wrapper} className="relative">
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="inline-flex items-center gap-2 rounded-lg border border-cream-200 bg-white px-3 py-2
                     text-sm text-ink transition hover:border-teal-300"
        >
          <CalendarDays className="size-4 text-muted" />
          {selected ? format(selected, 'EEE d MMM yyyy') : <span className="text-faint">No due date</span>}
        </button>

        {selected && (
          <button
            type="button"
            onClick={() => {
              onChange(null)
              setOpen(false)
            }}
            aria-label="Clear due date"
            title="Clear due date"
            className="grid size-8 place-items-center rounded-lg text-muted transition hover:bg-cream-100 hover:text-flame"
          >
            <X className="size-4" />
          </button>
        )}
      </div>

      {open && (
        <div className="absolute top-full left-0 z-30 mt-2 rounded-xl bg-white p-2 shadow-lg ring-1 ring-cream-200">
          <DayPicker
            mode="single"
            locale={enAU}
            weekStartsOn={1}
            defaultMonth={selected}
            selected={selected}
            onSelect={(date) => {
              onChange(date ? toIsoDate(date) : null)
              setOpen(false)
            }}
          />
        </div>
      )}
    </div>
  )
}
